import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * TextFieldProps is a union (standard/filled/outlined), so intersect rather
 * than `extends` (an interface can't extend a union).
 */
type SearchFieldProps = TextFieldProps & {
    /**
     * Clears the field — rendered as an end button while `value` is non-empty.
     * Ignored when `endAdornment` is supplied.
     */
    onClear?: () => void;
    /**
     * An explicit end adornment (e.g. the data grid's `QuickFilterClear`), used
     * instead of the built-in `onClear` button.
     */
    endAdornment?: ReactNode;
};

/**
 * A filled search input with a leading search icon and a trailing clear button.
 * The icon turns info-coloured while the box holds a term, which is the app's
 * one way of saying "you are looking at a subset". `hiddenLabel` keeps the
 * placeholder vertically centred. Shared by the plain filter boxes (via
 * `onClear`) and the data-grid quick filter (via `endAdornment`).
 *
 * `aria-label` names the `<input>` itself: on the TextField it would land on the
 * wrapper, and screen readers would announce an unnamed text box.
 */
export default function SearchField({
    slotProps,
    onClear,
    endAdornment,
    value,
    'aria-label': ariaLabel,
    ...rest
}: SearchFieldProps) {
    const intl = useIntl();
    const clearLabel = intl.formatMessage({ id: 'grid.search.clear' });
    const hasValue = value !== undefined && value !== null && value !== '';

    const clearControl =
        endAdornment ??
        (onClear && hasValue ? (
            <IconButton
                edge="end"
                size="small"
                aria-label={clearLabel}
                onClick={onClear}
            >
                <ClearIcon fontSize="small" />
            </IconButton>
        ) : null);

    // slotProps.input may be an object or a callback; only an object can merge.
    const inputSlot =
        slotProps?.input && typeof slotProps.input === 'object'
            ? slotProps.input
            : {};
    const htmlInputSlot =
        slotProps?.htmlInput && typeof slotProps.htmlInput === 'object'
            ? slotProps.htmlInput
            : {};

    return (
        <TextField
            size="small"
            hiddenLabel
            value={value}
            {...rest}
            slotProps={{
                ...slotProps,
                htmlInput: { 'aria-label': ariaLabel, ...htmlInputSlot },
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            {/* A search box with something in it is a list
                                with rows missing, so the magnifier says so in
                                the same info tone the grids' filter marks use
                                (see ActiveToolButton). */}
                            <SearchIcon
                                fontSize="small"
                                color={hasValue ? 'info' : undefined}
                            />
                        </InputAdornment>
                    ),
                    endAdornment: clearControl ? (
                        <InputAdornment position="end">
                            {clearControl}
                        </InputAdornment>
                    ) : undefined,
                    ...inputSlot
                }
            }}
        />
    );
}
